import type { Crop } from "react-image-crop";

/**
 * Returns a Promise that resolves to a Blob of the cropped image.
 * Supports crop in 'px' (display pixels) or '%'.
 */
export function getCroppedImageBlob(
  image: HTMLImageElement,
  crop: Crop,
  outputSize = 400,
  mimeType: string = "image/jpeg",
  quality: number = 0.92
): Promise<Blob> {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const px = crop.unit === "%";
  const toNatX = (v: number) => (px ? (v / 100) * image.naturalWidth : v * scaleX);
  const toNatY = (v: number) => (px ? (v / 100) * image.naturalHeight : v * scaleY);
  const toNatW = (v: number) => (px ? (v / 100) * image.naturalWidth : v * scaleX);
  const toNatH = (v: number) => (px ? (v / 100) * image.naturalHeight : v * scaleY);

  const srcX = toNatX(crop.x);
  const srcY = toNatY(crop.y);
  const srcW = toNatW(crop.width);
  const srcH = toNatH(crop.height);

  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.reject(new Error("Canvas 2d context not available"));

  ctx.drawImage(
    image,
    srcX,
    srcY,
    srcW,
    srcH,
    0,
    0,
    outputSize,
    outputSize
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality
    );
  });
}

/** Convert a Blob to a File with a given name. */
export function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, { type: blob.type });
}
